import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldCheck } from '@fortawesome/pro-light-svg-icons/faShieldCheck';
import { TypedMemo } from '../../common/types';
import { OAuthConsentClientIdentityData } from './OAuthConsentClientIdentity';
import { OAuthConsentWarning } from './OAuthConsentWarning';
import { OAuthConsentHead } from './OAuthConsentHead';
import { OAuthConsentAccount } from './OAuthConsentAccount';
import { OAuthConsentPermissions } from './OAuthConsentPermissions';
import { OAuthConsentActions } from './OAuthConsentActions';
import { oauthConsentMessages as messages } from './oauthConsentMessages';
import { withBrandWord } from '../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole consent page; each part uses its own slice of it */
import styles from './OAuthConsentView.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The scopes `oauthConsent.getConsentInfo` returns. */
export type OAuthConsentScope = 'music24:read' | 'music24:write';

interface OAuthConsentCardProps {
  clientName: string;
  identity: OAuthConsentClientIdentityData;
  scopes: OAuthConsentScope[];
  onApprove: () => void;
  onDeny: () => void;
  isApproving: boolean;
  isDenying: boolean;
}

/**
 * The consent prompt itself (design: oauth-consent a1-d2): the warning for a name that poses as
 * a known app, who asks, which workspace it connects to, what it can see and do, the promise
 * that Music24 asks first, and the two ways out. Every plan can approve.
 */
const OAuthConsentCardComponent: React.FC<OAuthConsentCardProps> = ({
  clientName,
  identity,
  scopes,
  onApprove,
  onDeny,
  isApproving,
  isDenying,
}) => {
  const intl = useIntl();
  const canWrite = scopes.includes('music24:write');

  return (
    <>
      {identity.nameLooksLikeKnownApp && <OAuthConsentWarning />}
      <OAuthConsentHead clientName={clientName} identity={identity} canWrite={canWrite} />
      <OAuthConsentAccount />
      <OAuthConsentPermissions clientName={clientName} canWrite={canWrite} />
      {canWrite && (
        <div className={styles.promise}>
          <FontAwesomeIcon icon={faShieldCheck} className={styles.promiseIcon} />
          <span>{withBrandWord(intl.formatMessage(messages.promise))}</span>
        </div>
      )}
      <OAuthConsentActions
        isWarned={identity.nameLooksLikeKnownApp}
        onApprove={onApprove}
        onDeny={onDeny}
        isApproving={isApproving}
        isDenying={isDenying}
      />
    </>
  );
};

export const OAuthConsentCard = TypedMemo(OAuthConsentCardComponent);
